import { useCallback, useRef, useState, useEffect } from 'react';
import MDEditor, { commands } from '@uiw/react-md-editor';
import { api } from '../utils/api';
import { useThemeStore } from '../stores/theme';

// Custom toolbar commands - exclude alignment commands that break the editor
const customCommands = [
  commands.bold,
  commands.italic,
  commands.strikethrough,
  commands.hr,
  commands.group([commands.title1, commands.title2, commands.title3, commands.title4, commands.title5, commands.title6], {
    name: 'title',
    groupName: 'title',
    buttonProps: { 'aria-label': 'Insert title' },
  }),
  commands.divider,
  commands.link,
  commands.quote,
  commands.code,
  commands.codeBlock,
  commands.comment,
  commands.image,
  commands.table,
  commands.divider,
  commands.unorderedListCommand,
  commands.orderedListCommand,
  commands.checkedListCommand,
  commands.divider,
  commands.help,
];

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function MarkdownEditor({ value, onChange, placeholder }: MarkdownEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const theme = useThemeStore((state) => state.theme);

  // Detect effective dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      if (theme === 'dark') {
        setIsDark(true);
      } else if (theme === 'light') {
        setIsDark(false);
      } else {
        // System theme
        setIsDark(window.matchMedia('(prefers-color-scheme: dark)').matches);
      }
    };

    checkDarkMode();

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        checkDarkMode();
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  const handleImageUpload = useCallback(
    async (file: File) => {
      setUploading(true);
      try {
        const result = await api.uploadImage(file);
        const imageMarkdown = `![${file.name}](${result.url})`;
        onChange(value + '\n' + imageMarkdown);
      } catch (error) {
        console.error('Upload failed:', error);
        alert('Failed to upload image');
      } finally {
        setUploading(false);
      }
    },
    [value, onChange]
  );

  const handlePaste = useCallback(
    async (event: React.ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file) {
            await handleImageUpload(file);
          }
          break;
        }
      }
    },
    [handleImageUpload]
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      setDragActive(false);
      const files = event.dataTransfer?.files;
      if (!files) return;

      for (const file of files) {
        if (file.type.startsWith('image/')) {
          await handleImageUpload(file);
        }
      }
    },
    [handleImageUpload]
  );

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files) return;

      for (const file of files) {
        if (file.type.startsWith('image/')) {
          await handleImageUpload(file);
        }
      }

      // Reset input
      event.target.value = '';
    },
    [handleImageUpload]
  );

  return (
    <div
      onPaste={handlePaste}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className="relative"
      data-color-mode={isDark ? 'dark' : 'light'}
    >
      {/* Drag overlay */}
      {dragActive && (
        <div className="absolute inset-0 z-10 bg-primary-50 bg-opacity-90 border-2 border-dashed border-primary-400 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <svg className="w-12 h-12 mx-auto text-primary-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-primary-700 font-medium">Drop image here</p>
          </div>
        </div>
      )}

      {/* Upload indicator */}
      {uploading && (
        <div className="absolute inset-0 z-10 bg-white dark:bg-gray-800 bg-opacity-80 dark:bg-opacity-80 flex items-center justify-center rounded-lg">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
            <div className="spinner" />
            <span>Uploading image...</span>
          </div>
        </div>
      )}

      <style>{`
        .w-md-editor {
          border: 1px solid ${isDark ? '#374151' : '#e5e7eb'} !important;
          border-radius: 0.75rem !important;
          box-shadow: none !important;
          background-color: ${isDark ? '#1f2937' : '#ffffff'} !important;
        }
        .w-md-editor-toolbar {
          background: ${isDark ? '#374151' : '#f9fafb'} !important;
          border-bottom: 1px solid ${isDark ? '#4b5563' : '#e5e7eb'} !important;
          border-radius: 0.75rem 0.75rem 0 0 !important;
          padding: 8px 12px !important;
        }
        .w-md-editor-toolbar ul > li > button {
          color: ${isDark ? '#9ca3af' : '#6b7280'} !important;
          border-radius: 6px !important;
        }
        .w-md-editor-toolbar ul > li > button:hover {
          color: ${isDark ? '#f3f4f6' : '#111827'} !important;
          background: ${isDark ? '#4b5563' : '#e5e7eb'} !important;
        }
        .w-md-editor-content {
          border-radius: 0 0 0.75rem 0.75rem !important;
          background-color: ${isDark ? '#1f2937' : '#ffffff'} !important;
        }
        .w-md-editor-toolbar ul > li.active > button {
          background: ${isDark ? 'rgba(59, 130, 246, 0.3)' : '#dbeafe'} !important;
          color: ${isDark ? '#93c5fd' : '#2563eb'} !important;
        }
        .w-md-editor-text-pre {
          color: ${isDark ? '#f3f4f6' : '#111827'} !important;
        }
        .wmde-markdown {
          background-color: ${isDark ? '#1f2937' : '#ffffff'} !important;
          color: ${isDark ? '#f3f4f6' : '#111827'} !important;
        }
      `}</style>

      <MDEditor
        value={value}
        onChange={(val) => onChange(val || '')}
        preview="edit"
        height={450}
        commands={customCommands}
        textareaProps={{
          placeholder: placeholder || 'Start writing your article...',
        }}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Bottom toolbar */}
      <div className="flex items-center justify-between mt-3 px-1">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Add Image
          </button>
        </div>
        <div className="text-xs text-gray-400 dark:text-gray-500">
          Markdown supported • Paste or drag images
        </div>
      </div>
    </div>
  );
}
